import {objectFromEntries, pick} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {navigate} from 'BenchlingUtil';
import {
  NameTemplatePartType,
  RegistryLabelingStrategy,
  TagSchemaItemType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import EntityNameTemplateField from 'name-templates/EntityNameTemplateField';
import {NameTemplatePart} from 'name-templates/NameTemplateConstants';
import notifications from 'notifications';
import {ClientSchemaField, createClientSchemaField} from 'registry/field/FieldTable';
import LabelingStrategyConfiguration from 'registry/labeling-strategies/LabelingStrategyConfiguration';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {AddSchemaInterfaceModalQuery} from 'schemas/schema-interface/__generated/AddSchemaInterfaceModalQuery';
import {SchemaInterfaceSummary} from 'schemas/schema-interface/SchemaInterfaceConstants';
import {schemaInterfaceURL} from 'schemas/schema-interface/SchemaInterfaceDAO';
import {
  convertFromClientInterfaceFieldToSchemaField,
  convertGQLSchemaInterfaceFieldToClientInterfaceField,
  FIELDSET_CONTROLLED_FIELD_ATTRIBUTES,
} from 'schemas/schema-interface/SchemaInterfaceUtils';
import {schemaFieldsetName} from 'schemas/schema-interface/util/schemaFieldsetName';
import SchemaPrefixInput from 'schemas/SchemaPrefixInput';
import {getItemTypeDisplayName, itemTypeToFolderItemTypeAndSequenceType} from 'schemas/TagSchemaItemTypeUtil';
import ConstraintTable, {ConstraintTableType} from 'settings/registry/ConstraintTable';
import TagSchemaItemTypeSelector from 'settings/registry/TagSchemaItemTypeSelector';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';
import {capitalize} from 'util/NameUtil';

import AddSchemaInterfaceModalFieldTable from './AddSchemaInterfaceModalFieldTable';
import {coerceConstraintShape} from './SchemaInterfaceUtils';

type SchemaInterface = ExtractByTypename<
  AddSchemaInterfaceModalQuery['schemaInterface'],
  'SchemaInterfaceNode'
>;

export type ImplementingSchemaField = SchemaField | ClientSchemaField;
export interface ImplementingSchemaInfo {
  fields: Array<ImplementingSchemaField>;
}

export interface NameTemplateOverride {
  parts: Array<NameTemplatePart>;
  fieldsUsedInParts: Array<ImplementingSchemaField>;
}

export interface SchemaInterfaceConformityUpdate {
  fields: Array<ImplementingSchemaField>;
  itemType?: TagSchemaItemType | null;
  prefix?: string;
  labelingStrategies?: Array<RegistryLabelingStrategy>;
  nameTemplate?: NameTemplateOverride;
}

export interface AddSchemaInterfaceRequestSubmitParams {
  schemaInterface: SchemaInterfaceSummary;
  conformityUpdate: SchemaInterfaceConformityUpdate;
}

export interface AddSchemaInterfaceModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (params: AddSchemaInterfaceRequestSubmitParams) => Promise<void>;
  schemaInterfaceId: string;
  tagSchema: ImplementingSchemaInfo;
}

export interface QueriedAddSchemaInterfaceModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (params: AddSchemaInterfaceRequestSubmitParams) => Promise<void>;
  schemaInterface: SchemaInterface;
  tagSchema: ImplementingSchemaInfo;
}

export const ADD_SCHEMA_INTERFACE_MODAL_QUERY: DocumentNode = gql`
  query AddSchemaInterfaceModalQuery($id: StrictString!) @skipAudit @engComponent(name: SCHEMAS) {
    schemaInterface(id: $id) {
      ... on SchemaInterfaceNode {
        id
        name
        itemType
        fields {
          id
          apiId
          systemName
          displayName
          fieldType
          isRequired
          isMulti
          isComputed
          schemaFieldSelectorId
          requiredLinkJson
          tooltipText
          unit {
            id
            symbol
          }
        }
        constraint {
          id
          fieldsJson
          uniqueResidues
          areUniqueResiduesCaseSensitive
          uniqueCanonicalSmiles
          status
          typeSpecificConstraintConfig {
            constraintType
          }
        }
        labelingStrategies
        prefix
        nameTemplates {
          linkedItemId
          templateType
          shouldOrderPartsBySequence
          nameTemplateParts {
            datetimeFormat
            fieldId
            text
            type
          }
        }
        organization {
          id
          urlIdentifier
        }
      }
    }
  }
`;

export function AddSchemaInterfaceModal({
  schemaInterface,
  tagSchema,
  ...props
}: QueriedAddSchemaInterfaceModalProps): JSX.Element {
  const {folderItemType, sequenceType} = schemaInterface.itemType
    ? itemTypeToFolderItemTypeAndSequenceType(schemaInterface.itemType)
    : {folderItemType: null, sequenceType: null};
  const prefixNotice = (
    <SchemaPrefixInput
      isBeingCreated={false}
      isWritable={false}
      onPrefixChange={() => null}
      prefix={schemaInterface.prefix}
      isRequired={false}
    />
  );

  const fields = schemaInterface.fields.map((field) =>
    convertGQLSchemaInterfaceFieldToClientInterfaceField(field)
  );

  const tagSchemaFieldNames = _.indexBy(tagSchema.fields, 'displayName');
  const missingFields = fields.filter((field) => !tagSchemaFieldNames.hasOwnProperty(field.displayName));
  const existingFields = fields.filter((field) => tagSchemaFieldNames.hasOwnProperty(field.displayName));

  const interfaceFieldNames = _.indexBy(schemaInterface.fields, 'displayName');
  // TODO (BNCH-117558): Make this robust to duplicate display names. In particular, only one of the two duplicated fields should be overridden, the other should be left alone.
  const tagSchemaOnlyFields: Array<ImplementingSchemaField> = tagSchema.fields.filter(
    (field) => !interfaceFieldNames.hasOwnProperty(field.displayName)
  );

  const missingFieldsWithOriginalField: Array<ImplementingSchemaField & {originalField: null}> =
    missingFields.map((field) => ({
      ...createClientSchemaField(''), // Note that every time this runs a new CID is generated.
      ...pick(convertFromClientInterfaceFieldToSchemaField(field), ...FIELDSET_CONTROLLED_FIELD_ATTRIBUTES),
      originalField: null,
    }));

  const existingFieldsWithOriginalField: Array<
    ImplementingSchemaField & {originalField: ImplementingSchemaField}
  > = existingFields.map((field) => {
    const originalField = tagSchemaFieldNames[field.displayName];
    assert(originalField != null, 'Unexpectedly cannot find matching field in tag schema.');
    return {
      ...originalField, // First load in the original field as not all properties are maintained by the fieldset, we want to keep them around.
      ...pick(convertFromClientInterfaceFieldToSchemaField(field), ...FIELDSET_CONTROLLED_FIELD_ATTRIBUTES),
      originalField,
    };
  });

  const fieldsNotice = (
    <>
      <div className="settingsPageContainer-label">{`${schemaInterface.name} ${schemaFieldsetName()}-managed fields`}</div>
      {missingFields.length > 0 ? (
        <AddSchemaInterfaceModalFieldTable
          ownerId={schemaInterface.organization.id}
          ownerUrlId={schemaInterface.organization.urlIdentifier}
          fields={missingFieldsWithOriginalField}
          title="New fields"
          titleTooltip="These fields will be automatically added to the schema to match the fieldset definition."
          dataTestAttrs={{component: 'FieldTable', element: 'new-fields'}}
        />
      ) : null}
      {existingFields.length > 0 ? (
        <AddSchemaInterfaceModalFieldTable
          ownerId={schemaInterface.organization.id}
          ownerUrlId={schemaInterface.organization.urlIdentifier}
          fields={existingFieldsWithOriginalField}
          title="Existing fields"
          titleTooltip="These fields will be automatically updated on the schema to match the fieldset definition."
          dataTestAttrs={{component: 'FieldTable', element: 'existing-fields'}}
        />
      ) : null}
    </>
  );
  const constraintNotice = (
    <ConstraintTable
      constraint={coerceConstraintShape(schemaInterface.constraint)}
      folderItemType={folderItemType}
      onAddClick={() => null}
      onRemoveClick={() => null}
      writable={false}
      tableType={ConstraintTableType.INTERFACE}
    />
  );

  const graphQLNameTemplate =
    schemaInterface.nameTemplates.length > 0 ? schemaInterface.nameTemplates[0] : null;
  const graphQLNameTemplateParts = graphQLNameTemplate != null ? graphQLNameTemplate.nameTemplateParts : [];

  const nameTemplateParts: Array<NameTemplatePart> = graphQLNameTemplateParts.map(
    (graphQLNameTemplatePart) =>
      objectFromEntries(
        Object.entries(graphQLNameTemplatePart).filter(([k, v]) => v != null && k !== '__typename')
      ) as NameTemplatePart
  );

  const itemType = schemaInterface.itemType;

  const nameTemplateNotice = (
    <EntityNameTemplateField
      nameTemplateParts={nameTemplateParts}
      schemaFields={fields.map((field) => convertFromClientInterfaceFieldToSchemaField(field))}
      schemaId={schemaInterface.id}
      bioentityType={folderItemType || ''}
      sequenceType={sequenceType}
      isWritable={false}
      setNameTemplateParts={(nameTemplateParts) => null}
      shouldOrderNamePartsBySequence={graphQLNameTemplate?.shouldOrderPartsBySequence || false}
      labelingStrategies={schemaInterface.labelingStrategies}
      itemType={itemType}
    />
  );

  const labelingStrategiesNotice = (
    <div className="u-spacing-top">
      <LabelingStrategyConfiguration
        enforceLabelingStrategies={true}
        labelingStrategies={schemaInterface.labelingStrategies}
        onChange={(labelingStrategies) => null}
        nameTemplateParts={nameTemplateParts}
        isWritable={false}
      />
    </div>
  );
  const interfaceLink = schemaInterfaceURL(schemaInterface.id);

  const interfaceWillApplyConfiguration =
    Boolean(schemaInterface.prefix) ||
    schemaInterface.itemType != null ||
    nameTemplateParts.length > 0 ||
    schemaInterface.labelingStrategies.length > 0 ||
    schemaInterface.fields.length > 0 ||
    schemaInterface.constraint != null;

  return (
    <Modal
      enableRedesign={true}
      title={`Add ${schemaFieldsetName()}`}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        const missingFields: Array<ImplementingSchemaField> = missingFieldsWithOriginalField.map(
          (field): ImplementingSchemaField => {
            const {originalField, ...updatedField} = field;
            return {
              ...updatedField,
              appliedFieldset: {
                id: schemaInterface.id,
                name: schemaInterface.name,
              },
            };
          }
        );
        const existingFields: Array<ImplementingSchemaField> = existingFieldsWithOriginalField.map(
          (field): ImplementingSchemaField => {
            const {originalField, ...updatedField} = field;
            return {
              ...updatedField,
              appliedFieldset: {
                id: schemaInterface.id,
                name: schemaInterface.name,
              },
            };
          }
        );

        let nameTemplate: NameTemplateOverride | undefined;
        if (nameTemplateParts) {
          const usedFieldIds = new Set(
            nameTemplateParts
              .filter((part) => part.type === NameTemplatePartType.FIELD)
              .map((part) => part.fieldId)
          );

          const fieldsUsedInParts = fields
            .filter((field) => field.apiId && usedFieldIds.has(field.apiId))
            .map((field) => convertFromClientInterfaceFieldToSchemaField(field));

          nameTemplate = {
            parts: nameTemplateParts,
            fieldsUsedInParts,
          };
        }

        props.onRequestSubmit({
          schemaInterface: {
            id: schemaInterface.id,
            name: schemaInterface.name,
          },
          conformityUpdate: {
            itemType,
            fields: [...tagSchemaOnlyFields, ...missingFields, ...existingFields],
            prefix: schemaInterface.prefix,
            labelingStrategies: schemaInterface.labelingStrategies,
            nameTemplate,
          },
        });
      }}
      closeableOnBackgroundClick={true}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
        },
        {
          text: 'Add',
          submit: true,
        },
      ]}
    >
      {interfaceWillApplyConfiguration ? (
        <div>
          <p>
            {capitalize(schemaFieldsetName())}{' '}
            <a
              href={interfaceLink}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                navigate(e);
                props.onRequestClose();
              }}
            >
              {schemaInterface.name}
            </a>{' '}
            will set these configurations for this schema:
          </p>
          {schemaInterface.prefix ? prefixNotice : null}
          {itemType ? (
            <TagSchemaItemTypeSelector
              displayName={getItemTypeDisplayName(itemType)}
              disabled={true}
              selectedItemType={itemType}
              onChangeItemType={() => {}}
              showLabel={true}
            />
          ) : null}
          {schemaInterface.constraint ? constraintNotice : null}
          {schemaInterface.fields.length > 0 ? fieldsNotice : null}
          {nameTemplateParts.length > 0 ? nameTemplateNotice : null}
          {schemaInterface.labelingStrategies.length > 0 ? labelingStrategiesNotice : null}
        </div>
      ) : (
        <p>This {schemaFieldsetName()} will not affect any configurations for this schema.</p>
      )}
    </Modal>
  );
}

export default function QueryingAddSchemaInterfaceModal(
  props: AddSchemaInterfaceModalProps
): JSX.Element | null {
  const interfaceQueryResult = useBenchlingQuery<AddSchemaInterfaceModalQuery>(
    ADD_SCHEMA_INTERFACE_MODAL_QUERY,
    {
      variables: {id: props.schemaInterfaceId},
    }
  );
  if (interfaceQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (interfaceQueryResult.type === QueryResultType.ERROR) {
    notifications.error(`Could not load ${schemaFieldsetName()}; try reloading the page.`);
    return null;
  }

  assert(
    !(interfaceQueryResult.data && isInaccessible(interfaceQueryResult.data.schemaInterface)),
    `Expected to add a fieldset, but it was inaccessible (schemaInterfaceId=${props.schemaInterfaceId})`
  );
  const schemaInterface: SchemaInterface = interfaceQueryResult.data.schemaInterface as SchemaInterface;

  return (
    <div data-test-component="AddSchemaInterfaceModal" data-test-element="root">
      <AddSchemaInterfaceModal schemaInterface={schemaInterface} {...props} />
    </div>
  );
}
